import {
  ALERT_CLOSE_REASON,
  createAlertTracker,
  type AlertShowing
} from '#~/analytics/domains/alert-lifecycle.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { useFeatureFlag } from '#~/posthog/index.js';
import { broker } from '#~/agent/broker.js';
import {
  enhanceDiscoveredService,
  type DiscoveredService,
  type SystemInfo
} from '#~/agent/discovery/utils.js';
import { useAgents } from '#~/agent/hooks.js';
import { fieldKeyedAgentsEqual, idNameStatusPick } from '#~/agent/provider/use-agents-field-eq.js';
import { ElectronAPI } from '#~/electron/api.js';
import { isElectronApp } from '#~/electron/utils.js';
// Per IR-104 plan (e2e-tests harness STag-only convention): the bridge popover
// toggle needs a stable selector so the Playwright e2e suite can drive it
// without text/role/structural fallbacks. STagNew is the harness-blessed
// attribute helper. See: ai.knowledge/knowledge/platform/electron-vite-app
// + studio/electron/e2e/helpers/selectors.ts lines 1-4.
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { Button } from '@studio/design-system/button';
import * as Popover from '@studio/design-system/popover';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import type { Agent } from '@studio/models/agent';
import { shortCircuit } from '@studio/react-utils/event';
import { useMount } from '@studio/react-utils/hooks';
import { OperatingSystem } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { isNotEmpty } from '@tryiris-ai/js-types';
import { clsx } from 'cn';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { twMerge } from 'cn';
import { usePopupStore } from '../../../store/index.js';
import {
  useAgentsSystemInfo,
  useGetMachineAgentId,
  usePermissions,
  useRoomAgent
} from './hooks.js';
import { ConnectedBridge, SearchDevices, Welcome } from './screens/index.js';
import type { Props } from './types.js';
import { isBridgeDetailAvailable } from './detail-availability.js';

// Constants for timeouts and delays
const SYSTEM_INFO_TIMEOUT_MS = 7000;

/** `<Bridge>` component. */
export const Bridge = ({ children, className, ...props }: Props): ReactElement => {
  const { popup, setPopup } = usePopupStore();
  const { canAccessBridge } = usePermissions();

  return (
    <Popover.Root
      open={popup === 'bridge'}
      onOpenChange={(open) => setPopup(open ? 'bridge' : false)}
    >
      {/*
        Per IR-104 plan W1 acceptance criterion #2: stable selector for the
        bridge popover toggle. The harness convention is STag-only — text/role
        fallbacks are not acceptable for interactive selectors
        (see studio/electron/e2e/helpers/selectors.ts:1-4). Sel.BridgeManagement
        already exists in the source enum at src/stag/tag.ts:35.
      */}
      <Popover.Toggle
        icon='bridge'
        disabled={!canAccessBridge}
        {...STagNew(Sel.BridgeManagement, 'toggle')}
        {...props}
      />
      {/*
        The popover portals out of the trigger's subtree, so tests need a
        stable handle on the floating panel to scope their reads to it.
        Tagging the panel replaces role/text scoping (`getByRole('dialog')`)
        which would also match any other Radix layer that happens to be open.
        Per rule: playwright-stag-selectors-only (ai.knowledge/platform).
      */}
      <Popover.Content
        className={twMerge('z-50 w-[350px] overflow-hidden', className)}
        collisionPadding={16}
        onInteractOutside={shortCircuit}
        sideOffset={10}
        {...DataSTag(Sel.BridgeManagement, 'popover')}
      >
        <BridgeContent />
      </Popover.Content>
    </Popover.Root>
  );
};

/**
 * @internal `BridgeContent`
 *
 * @description The content of the `Bridge` component. Renders different screens based on the state.
 */
const BridgeContent = () => {
  const unavailableShowings = useRef(new Map<Agent['id'], AlertShowing | null>());
  const { popup } = usePopupStore();
  const { track } = useAnalytics();
  const { enabled: unavailableEnabled, loading: unavailableLoading } = useFeatureFlag(
    'alert_n1_enabled',
    { defaultValue: true }
  );
  const panelOpen = popup === 'bridge';
  const showUnavailable = unavailableEnabled === true && !unavailableLoading;

  useEffect(() => {
    // Radix retains children during exit animation; a closed panel is already
    // a finished visit, even if it reopens before those children unmount.
    if (!panelOpen) unavailableShowings.current.clear();
  }, [panelOpen]);
  const [screen, setScreen] = useState<'welcome' | 'search' | 'connected'>('welcome');
  const [discoveredServices, setDiscoveredServices] = useState<DiscoveredService[]>([]);

  const [selectedAgentId, setSelectedAgentId] = useState<Agent['id'] | null>(null);
  const [selfDiscoerySystemInfo, setSelfDiscoerySystemInfo] = useState<SystemInfo | null>(null);

  const [loadSystemInfo, setLoadSystemInfo] = useState(true);

  // Reference to store cleanup functions for mDNS listeners
  const mDNSListenerCleanup = useRef<(() => void) | null>(null);

  // All non-embedded agents:
  const agents = useAgents(
    (a) => a.filter((a) => !a.embedded).map((a) => ({ id: a.id, name: a.name, status: a.status })),
    (left, right) => fieldKeyedAgentsEqual(left, right, idNameStatusPick)
  );

  // Agent(s) system info (this info will only be populated for agents that are online AND are linked)
  const agentSystemInfoResult = useAgentsSystemInfo(agents);
  const agentsOperatingSystemAndMachineType =
    agentSystemInfoResult?.agentsOperatingSystemAndMachineType ?? {};
  const agentSystemInfo = agentSystemInfoResult?.systemInfo ?? {};

  // Machine agent:
  const { agentId: machineAgentId } = useGetMachineAgentId();

  // Room agent:
  const roomAgent = useRoomAgent(machineAgentId);

  const allAgentStats = roomAgent?.wiring.summary.agentStats;
  useEffect(() => {
    if (!panelOpen || !showUnavailable) return;
    const tracker = createAlertTracker(track);
    // Keep observing shown Bridges while another Bridge or search is visible.
    for (const [id, showing] of unavailableShowings.current) {
      const stats = allAgentStats?.[id];
      if (
        showing &&
        isBridgeDetailAvailable(stats?.cpuUsage) &&
        isBridgeDetailAvailable(stats?.ramUsage) &&
        isBridgeDetailAvailable(stats?.diskSpace)
      ) {
        // Retain the key after recovery: once per Bridge per panel visit.
        unavailableShowings.current.set(id, null);
        tracker.trackAlertClosed(showing, ALERT_CLOSE_REASON.RESOLVED);
      }
    }
  }, [allAgentStats, panelOpen, showUnavailable, track]);

  const agentId = roomAgent?.agent.id;
  const agentOS = agentId ? agentsOperatingSystemAndMachineType[agentId] : OperatingSystem.OS_MAC;
  const canSearchCancel = (screen === 'search' && roomAgent) || false;
  const isPtzoptics = vendorName().toLowerCase() === 'hive';

  const getMachineOperatingSystem = () => {
    // @ts-expect-error
    return agentOS.os === OperatingSystem.OS_WINDOWS ? 'Windows Machine' : 'Mac Machine';
  };

  // Listen for bridge.selected events from bridge-selector dropdown
  useMount(() => {
    const proxies = agents.map((agent) => broker.acquire(agent.id)).filter(isNotEmpty);

    const fn = (agentId: Agent['id'] | null) => {
      setSelectedAgentId(agentId);
    };

    proxies.forEach((proxy) => proxy.on('bridge.selected', fn));
    return () => {
      proxies.forEach((proxy) => proxy.off('bridge.selected', fn));
    };
  });

  // Set up mDNS IPC event listeners
  useMount(() => {
    if (!isElectronApp()) return;

    // Set up listeners for Iris-specific mDNS services
    const irisMDNSCleanup = ElectronAPI.mDNS.onIrisServiceDiscovered((service) => {
      setDiscoveredServices((prev) => {
        // For the `bridge` tray view, we only render desktop agent services
        if (service.data?.bridgeType !== 'd') return prev;

        // Enhance the service with device type information before adding to state
        const enhancedService = enhanceDiscoveredService(service);

        // find the index of the service in the list
        const index = prev.findIndex((s) => s.data?.machineId === service.data?.machineId);

        // if the service is already in the list, update it
        if (index !== -1) {
          const updatedServices = [...prev];
          updatedServices[index] = enhancedService;
          return updatedServices;
        }

        // if the service is not in the list, add it
        return [...prev, enhancedService];
      });
    });

    if (mDNSListenerCleanup.current !== null) {
      mDNSListenerCleanup.current();
    }

    // Store cleanup functions (could be expanded to include generic mDNS services)
    mDNSListenerCleanup.current = irisMDNSCleanup;

    // Cleanup listeners on unmount
    return () => {
      mDNSListenerCleanup.current?.();
    };
  });

  // When search screen is shown, trigger a new mDNS query and fetch system info
  useEffect(() => {
    if (!isElectronApp() || screen !== 'search') {
      return undefined;
    }

    ElectronAPI.mDNS.queryIris();
    // Fetch system information when searching for devices. The timeout must
    // RACE the invoke: the prior shape only armed the 7s timer inside
    // .catch(), so a system:info IPC that never settles (observed on Windows
    // Hive-itg 2026-08-07 — 8 invokes, zero completions, search screen wedged
    // on "Loading system information…" indefinitely) flipped neither branch
    // and loadSystemInfo stayed true forever. System info is a cosmetic
    // enhancement (device-type icons), so degrade to null and render the
    // search UI rather than block discovery on it.
    // Per rule: e2e-tests-fail-fast-pass-fast analog for product waits +
    // reference-rules-in-comments (ai.knowledge/global).
    let cancelled = false;
    const timeout = new Promise<null>((resolve) => {
      setTimeout(() => resolve(null), SYSTEM_INFO_TIMEOUT_MS);
    });
    Promise.race([ElectronAPI.getSystemInfo(), timeout])
      .then((systemInfo) => {
        if (cancelled) return;
        if (systemInfo === null) {
          console.warn(
            `System information did not arrive within ${SYSTEM_INFO_TIMEOUT_MS}ms — proceeding without it`
          );
        } else {
          setSelfDiscoerySystemInfo(systemInfo);
        }
        setLoadSystemInfo(false);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to get system information:', error);
        setLoadSystemInfo(false);
      });
    return () => {
      cancelled = true;
    };
  }, [screen]);

  const machineName = (roomAgent?.agent.name || undefined) ?? getMachineOperatingSystem();
  // Removed unused variables related to auto-linking

  /*
  This `useEffect` was initially created when we only supported a single agent.
  It now handles an arbitrary number of agents.
  */
  useEffect(() => {
    if (screen === 'welcome' && (roomAgent || agents.length > 0)) {
      setScreen('connected');
    }
    if (screen === 'connected' && !roomAgent && agents.length === 0) {
      setScreen('search');
    }
  }, [roomAgent?.agent, agents]);

  // Note: Auto-linking logic has been moved to AutoLinkProvider at the app level
  // This component now only handles manual bridge selection and display

  const renderBridgeTrayView = () => {
    switch (screen) {
      case 'welcome':
        return <Welcome onSearch={() => setScreen('search')} />;

      // Removed 'auto-linking' case as functionality moved to AutoLinkProvider

      case 'search':
        return (
          <SearchDevices
            agents={agents}
            selfDiscoerySystemInfo={selfDiscoerySystemInfo}
            agentSystemInfo={agentSystemInfo}
            roomAgentId={roomAgent?.agent?.id}
            discoveredServices={discoveredServices}
            isPtzoptics={isPtzoptics}
            machineName={machineName}
            loadSystemInfo={loadSystemInfo}
          />
        );

      case 'connected':
        if (!roomAgent) return null;

        return (
          <ConnectedBridge
            unavailableShowings={unavailableShowings.current}
            showUnavailable={showUnavailable}
            panelOpen={panelOpen}
            agents={agents}
            roomAgent={roomAgent}
            selectedAgentId={selectedAgentId}
            agentSystemInfo={agentSystemInfo}
            onSearch={() => setScreen('search')}
            machineName={machineName}
            isPtzoptics={isPtzoptics}
          />
        );

      default:
        return <Welcome onSearch={() => setScreen('search')} />;
    }
  };

  return (
    <>
      <div
        className={clsx('flex h-48 items-center justify-between border-b-1 px-16', {
          'border-base-50': !isPtzoptics,
          'border-[#46464B]': isPtzoptics
        })}
      >
        <Popover.Title>Bridges ({agents.length})</Popover.Title>
        {canSearchCancel ? (
          <Button
            variant='link'
            {...STagNew(Sel.BridgeManagement, 'cancel-search')}
            className='text-foreground hover:text-foreground hover:text-opacity-90'
            onClick={() => {
              setScreen('connected');
            }}
          >
            Cancel
          </Button>
        ) : (
          <Popover.Dismiss />
        )}
      </div>
      {renderBridgeTrayView()}
    </>
  );
};
